import { useNavigation, useRoute } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, AlertButton, TouchableOpacity } from 'react-native';

import { Alert } from '@components/Alert/index.web';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/RestyleIcon';
import { requestPasswordReset } from '@utils/requestPasswordReset';

export const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const [resetLoadingState, setResetLoadingState] = useState<boolean>(false);
  const route = useRoute();
  // `forgot-password` is not in the linking config, so a browser reload or a
  // direct URL hit lands here with no params at all. Interpolating an empty
  // address would render "If  has a TaskTag account…" — a missing subject and
  // a double space — and resend is a dead control in that state (TA-2592 review).
  //
  // `useRoute()` carries no generic here, so `params.email` is untyped: the
  // narrowing is what keeps a non-string param from throwing on `.trim()`,
  // rather than just falling through to the no-address branch.
  const emailParam = route?.params?.email;
  const xemail = typeof emailParam === 'string' ? emailParam.trim() : '';
  const hasAddress = xemail.length > 0;

  const [countdown, setCountdown] = useState(30);
  const [isActive, setIsActive] = useState(true);

  const startCountdown = () => {
    setCountdown(30);
    setIsActive(true);
  };

  const stopCountdown = () => {
    setIsActive(false);
  };

  useEffect(() => {
    let _timer: NodeJS.Timer;

    if (isActive && countdown > 0) {
      _timer = setInterval(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setIsActive(false);
    }

    return () => clearInterval(_timer);
  }, [isActive, countdown]);

  const alertWithOkay = (message: string) => {
    const okayButton: AlertButton = {
      text: t('shared:okay'),
      style: 'cancel',
    };
    Alert.alert(t('shared:error'), message, [okayButton]);
  };

  const forgotPassword = async () => {
    if (!hasAddress) return;
    if (resetLoadingState) return;

    setResetLoadingState(true);
    const result = await requestPasswordReset(xemail);
    setResetLoadingState(false);

    // TA-2592: this screen is reachable with an unregistered address, so
    // relaying the backend's answer *about the address* here would leak
    // exactly what the login form no longer does. The three cases below are
    // all independent of whether an account exists, so each is safe to show.
    if (result === 'unavailable') {
      alertWithOkay(t('shared:cloudflareEdgeErrorMsg'));
      return;
    }

    // TA-10679: the rate limit is keyed on the address, so it fires the same
    // way with or without an account. The countdown deliberately does NOT
    // restart — restarting reads as "an email is on its way", and none was
    // sent. Leaving the resend link live lets the user retry once it lapses.
    if (result === 'throttled') {
      alertWithOkay(t('shared:tooManyResetAttempts'));
      return;
    }

    if (result === 'invalid') {
      alertWithOkay(t('shared:checkTheEmailAddress'));
      return;
    }

    startCountdown();
  };

  return (
    <Box backgroundColor='grey01' height='100%'>
      <Box alignItems='center' justifyContent='center' height='100%'>
        <Box
          alignItems='center'
          borderRadius='m'
          backgroundColor='white'
          style={{
            paddingVertical: 32,
            paddingHorizontal: 40,
            shadowColor: 'black',
            shadowOpacity: 0.05,
            shadowOffset: { width: 0, height: 25 },
            shadowRadius: 25,
            position: 'absolute',
            width: 440,
            overflow: 'hidden',
          }}>
          <Icon name='Logo' width={142} height={43} />
          <Text
            style={{ marginTop: 40 }}
            mb='xs'
            variant='heading2'
            color='black'>
            {t('shared:resetPassword')}
          </Text>
          {/* TA-10679: the backend answers identically for an address with
              and without an account, so this screen is reached either way.
              The conditional phrasing is what makes the sentence true in both
              cases, and echoing the address back — the user's own input, so
              no information to an attacker — is what lets someone who typo'd
              spot it. */}
          <Text variant='webBodySecondary' color='black' textAlign='center'>
            {hasAddress
              ? t('models:onBoarding.modal.resetLinkSentIfAccountExists', {
                  email: xemail,
                })
              : t(
                  'models:onBoarding.modal.resetLinkSentIfAccountExistsNoAddress'
                )}
          </Text>
          <Box mt='l' height={48}>
            <Text variant='webBodySecondary' color='black'>
              {t('models:onBoarding.modal.openEmailApp')}
            </Text>
          </Box>
          {/* Without an address there is nothing to resend to — `forgotPassword`
              early-returns, so the link would look live and do nothing. */}
          <Box alignItems='center' flexDirection='row'>
            {hasAddress && (countdown == 0 || !isActive) && (
              <Text variant='webBodySecondary' color='textSecondary'>
                {t('models:onBoarding.modal.didntReceiveTheEmail')}{' '}
                <Text
                  variant='webBodySecondary'
                  color='greenSecondary'
                  disabled={resetLoadingState}
                  onPress={forgotPassword}>
                  {t('models:onBoarding.modal.resendEmail')}
                </Text>
              </Text>
            )}
            {hasAddress &&
              (countdown == 0 || !isActive) &&
              resetLoadingState && (
                <Box marginLeft='s'>
                  <ActivityIndicator style={{ height: 16 }} />
                </Box>
              )}
            {hasAddress && countdown > 0 && isActive && (
              <Text variant='webBodySecondary' color='textSecondary'>
                {t('models:onBoarding.modal.resendEmailIn', {
                  time:
                    (countdown.toString().length == 1 ? '00:0' : '00:') +
                    countdown.toString(),
                })}
              </Text>
            )}
          </Box>

          <TouchableOpacity
            onPress={() => {
              stopCountdown();
              navigation.goBack();
            }}>
            <Box
              flexDirection='row'
              mt='l'
              height={48}
              alignItems='center'
              accessibilityLabel={t('models:onBoarding.modal.backToLogin')}>
              <Icon name='ArrowLeft' variant='s' marginRight='xs' />
              <Text variant='labelSmall' color='black'>
                {t('models:onBoarding.modal.backToLogin')}
              </Text>
            </Box>
          </TouchableOpacity>
        </Box>
      </Box>
    </Box>
  );
};
